(() => {
  const STORAGE_KEY = 'kkmAdaptiveContextV1';
  const SESSION_PREFIX = 'kkmAdaptiveSeen:';
  const MASTER_FEEDS = new Set([
    'grocery-kitchen',
    'snacks-drinks-food',
    'beauty-personal-care',
    'home-care',
    'stationery-games'
  ]);

  const safeParse = (value, fallback) => {
    try {
      return value ? JSON.parse(value) : fallback;
    } catch {
      return fallback;
    }
  };

  const readState = () => {
    const fallback = { feeds: {} };
    try {
      const parsed = safeParse(localStorage.getItem(STORAGE_KEY), fallback);
      if (!parsed || typeof parsed !== 'object') return fallback;
      if (!parsed.feeds || typeof parsed.feeds !== 'object') parsed.feeds = {};
      return parsed;
    } catch {
      return fallback;
    }
  };

  const writeState = (state) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  };

  const recordFeedView = (feedKey) => {
    if (!MASTER_FEEDS.has(feedKey)) return;

    const pathKey = `${SESSION_PREFIX}${location.pathname}`;
    try {
      if (sessionStorage.getItem(pathKey) === feedKey) return;
      sessionStorage.setItem(pathKey, feedKey);
    } catch {}

    const state = readState();
    const current = state.feeds[feedKey] || { count: 0, last: 0 };
    state.feeds[feedKey] = {
      count: Math.min(20, Number(current.count || 0) + 1),
      last: Date.now()
    };
    writeState(state);
  };

  const recentCookieExists = () => {
    return document.cookie
      .split(';')
      .some((part) => part.trim().startsWith('recentlyViewed='));
  };

  const getPreferredFeed = (minimumCount = 2, memoryDays = 60) => {
    const state = readState();
    const cutoff = Date.now() - (Math.max(1, Number(memoryDays || 60)) * 86400000);
    let winner = null;

    Object.entries(state.feeds || {}).forEach(([feedKey, data]) => {
      if (!MASTER_FEEDS.has(feedKey)) return;

      const count = Number(data?.count || 0);
      const last = Number(data?.last || 0);
      if (count < minimumCount || last < cutoff) return;

      if (!winner || count > winner.count || (count === winner.count && last > winner.last)) {
        winner = { feedKey, count, last };
      }
    });

    return winner;
  };

  const closestSection = (node) => node?.closest?.('.shopify-section') || null;

  const findFeedSection = (feedKey) => {
    const candidates = Array.from(document.querySelectorAll('[data-feed-key]'));
    const match = candidates.find((node) => node.dataset.feedKey === feedKey);
    return closestSection(match);
  };

  const moveAfter = (node, reference) => {
    if (!node || !reference || node === reference) return reference;
    reference.insertAdjacentElement('afterend', node);
    return node;
  };

  const applyHomepagePriority = () => {
    const config = document.querySelector('[data-kkm-adaptive-homepage]');
    if (!config || config.dataset.enabled !== 'true') return;

    const controllerSection = closestSection(config);
    if (!controllerSection) return;

    const customerState = config.dataset.customerState || 'anonymous';
    const threshold = Math.max(2, Number(config.dataset.interestThreshold || 2));
    const memoryDays = Math.max(1, Number(config.dataset.memoryDays || 60));
    const hasRecentlyViewed = recentCookieExists();
    const preferred = getPreferredFeed(threshold, memoryDays);
    const hasBehaviorHistory = hasRecentlyViewed || Boolean(preferred);

    if (customerState === 'anonymous' && !hasBehaviorHistory) {
      config.dataset.appliedSegment = 'new';
      return;
    }

    let cursor = controllerSection;
    const buyAgainSection = findFeedSection('buy-again');
    const recentlyViewedSection = findFeedSection('recently-viewed');

    if (
      customerState === 'purchaser' &&
      config.dataset.prioritizeBuyAgain === 'true' &&
      buyAgainSection
    ) {
      cursor = moveAfter(buyAgainSection, cursor);
    }

    if (
      hasRecentlyViewed &&
      config.dataset.promoteRecentlyViewed === 'true' &&
      recentlyViewedSection
    ) {
      cursor = moveAfter(recentlyViewedSection, cursor);
    }

    if (
      preferred &&
      config.dataset.promoteInterestFeed === 'true'
    ) {
      const preferredSection = findFeedSection(preferred.feedKey);
      if (preferredSection) {
        cursor = moveAfter(preferredSection, cursor);
        preferredSection.dataset.kkmAdaptivePriority = 'interest';
      }
    }

    config.dataset.appliedSegment =
      customerState === 'purchaser'
        ? 'purchaser'
        : hasBehaviorHistory
          ? 'returning'
          : 'identified';

    config.dataset.preferredFeed = preferred?.feedKey || '';

    document.dispatchEvent(new CustomEvent('kkm:adaptive-homepage-applied', {
      detail: {
        segment: config.dataset.appliedSegment,
        preferredFeed: config.dataset.preferredFeed
      }
    }));
  };

  const initialize = () => {
    const pageContext = document.querySelector('[data-kkm-customer-context]');
    const feedKey = pageContext?.dataset.feedKey || '';
    if (feedKey) recordFeedView(feedKey);

    applyHomepagePriority();
  };

  window.KKMAdaptiveContext = {
    readState,
    recordFeedView,
    getPreferredFeed,
    applyHomepagePriority
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', applyHomepagePriority);
})();
